import { formatCPF } from '../utils/cpfFormatter';

/**
 * Should this search keystroke be auto-formatted as a CPF, and what does the field become?
 *
 * The search bar masks CPFs as you type, which is worth keeping — operators paste bare digits
 * constantly. The bug was in deciding WHEN. `isCPFFormat` counted digits and ignored everything
 * else, so any input with three or more digits anywhere was declared a CPF and pushed through
 * `formatCPF`, which strips non-digits and truncates to 11:
 *
 *     "Ana 101"                 -> "101"               the name vanished mid-keystroke
 *     "user123@panther.com.br"  -> "123"               so did the address
 *     "123456789012345"         -> "123.456.789-01"    four digits silently dropped
 *
 * And it did not recover: the stripped value still held three digits, so every further letter
 * was eaten as well. Clearing the field was the only way out.
 *
 * The rule here is shape, not digit count. A CPF being typed contains nothing but digits and its
 * own punctuation; the moment a letter, space or @ appears the operator is plainly searching for
 * something else and the text is passed through untouched. Over 11 digits is likewise not a CPF
 * — more likely a phone or a document number — so it is left exactly as typed rather than cut
 * down to fit.
 *
 * Known ambiguity, deliberately left alone: a Brazilian mobile with DDD is also 11 bare digits,
 * so typing one still gets CPF punctuation. That is pre-existing behaviour and non-destructive
 * (no character is lost), and guessing between the two from length alone cannot be done here.
 */

/** Digits plus the punctuation a CPF itself carries. Nothing else. */
const CPF_SHAPED = /^[\d.-]+$/;

const MIN_CPF_DIGITS = 3;
const MAX_CPF_DIGITS = 11;

const digitCount = (value: string): number => value.replace(/\D/g, '').length;

/** True only when the field holds something that could be a CPF mid-entry. */
export const looksLikeCpfEntry = (raw: string): boolean => {
  if (!CPF_SHAPED.test(raw)) return false;
  const digits = digitCount(raw);
  return digits >= MIN_CPF_DIGITS && digits <= MAX_CPF_DIGITS;
};

/**
 * The value the search field should show for this keystroke.
 *
 * For CPF-shaped input the display is derived from the DIGITS, never from the punctuation already
 * sitting in the field. Masking in place looks equivalent but is not: once a value had been
 * masked at 11 digits, a 12th keystroke appended to the mask and left its separators stranded in
 * the middle of a plainly-not-a-CPF number ("123.456.789-012345"). Re-deriving means the field is
 * a pure function of what was typed, so the separators appear and disappear with the mask that
 * owns them.
 *
 * No digit or letter is ever dropped, which is the property that matters: a keystroke can no
 * longer erase text the operator can see.
 */
export const maskSearchValue = (raw: string): string => {
  if (!CPF_SHAPED.test(raw)) return raw;

  const digits = raw.replace(/\D/g, '');
  if (digits.length === 0) return raw;

  return looksLikeCpfEntry(raw) ? formatCPF(digits) : digits;
};

/**
 * The term to send to the API for what is currently displayed.
 *
 * The mask is presentation; the column is not masked. condo-manager normalizes CPF to bare digits
 * on write (utils.NormalizeCPF, on every person create/update path) and the people search runs
 *
 *     people.cpf ILIKE '%<term>%'  OR  people.rg ILIKE '%<term>%'
 *
 * so a masked "470.480" matched nothing against a stored "47048012345", and the same mask fires
 * on an RG (commonly 7-9 digits) and broke that too. Visitantes stripped the mask before querying
 * and worked; Pessoas, Moradores and Prestadores sent it verbatim and silently found nobody.
 *
 * Only strips when the field holds a CPF-shaped value, so a name keeps its spaces and an email
 * keeps its @ — the backend splits those into words and matches them on their own.
 */
export const searchQueryValue = (display: string): string =>
  looksLikeCpfEntry(display) ? display.replace(/\D/g, '') : display;
